import { useEffect, useState } from 'react';
import { formatTime } from '../util/datetime';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import ApiIcon from '@mui/icons-material/Api';
import MemoryIcon from '@mui/icons-material/Memory';
import WebIcon from '@mui/icons-material/Web';
import TabletAndroidIcon from '@mui/icons-material/TabletAndroid';
import VideocamIcon from '@mui/icons-material/Videocam';

import {
  getSystemStatus,
  type ServiceStatus,
  type SystemStatusSnapshot,
} from '../api/systemStatus';

/** Live-status → chip label + colour. */
const STATUS_META: Record<
  ServiceStatus,
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  ok: { label: 'Healthy', color: 'success' },
  stale: { label: 'Stale', color: 'warning' },
  down: { label: 'Down', color: 'error' },
};

/**
 * The platform's data flow, one tiny left-to-right diagram: what produces
 * data, what processes it, and where it lands. Static — it's documentation,
 * not a live topology.
 */
function ArchFlow() {
  const steps = ['Capture app', 'API', 'Queue', 'Insights worker', 'Database', 'Portal'];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        rowGap: 1.5,
        mt: 1,
      }}
    >
      {steps.map((s, i) => (
        <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
              fontSize: '0.78rem',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {s}
          </Box>
          {i < steps.length - 1 && (
            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 700 }}>
              →
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Top-level About page. Visible to every role from the sidebar.
 *
 * Three sections beyond the elevator pitch:
 *   1. **Meet the Team** — a link out to the team section on the
 *      corporate site (10newtons.com/#team); the roster is maintained
 *      there now rather than duplicated in-app.
 *   2. **Stack snapshot** — the libraries that power each container.
 *      Hardcoded against the four manifests at the time of writing
 *      (api/pyproject.toml, capture-app/package.json,
 *      portal/package.json, and the worker's [worker] extra).
 *      Bump these when you bump versions; an out-of-date list here
 *      is non-blocking — it's reader documentation, not runtime
 *      truth.
 *   3. **Copyright / legal** — © 10 Newtons Inc., contact email.
 *
 * Why hardcoded vs auto-generated from package.json: the About page
 * is intentionally curated copy — top-level libraries the operator
 * recognises, not the full transitive tree (~400 packages on the
 * portal alone). The runtime sees the real `package.json` regardless.
 */

interface StackComponent {
  name: string;
  /** Short tag rendered at the right of the card header — language /
   *  framework summary, monospace. */
  tag: string;
  blurb: string;
  /** Theme palette key for the card accent. Each stack gets a
   *  distinct tint so the four cards read as siblings at a glance,
   *  not a uniform wall. */
  accent: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  icon: React.ReactNode;
  libs: { name: string; what: string }[];
}

const STACKS: StackComponent[] = [
  {
    name: 'API',
    tag: 'Python · FastAPI',
    accent: 'primary',
    icon: <ApiIcon />,
    blurb:
      'Multi-tenant HTTP surface: FastAPI on uvicorn worker processes; SQLAlchemy 2.0 async with asyncpg against a schema-per-tenant Postgres (TLS on every connection). Owns auth (user + device JWT, TOTP MFA, superadmin act-as sessions, a per-participant “Allow sign-in” switch; SAML/OIDC SSO is built but not yet switched on) and the full route surface — sessions, scoring reads, procedures/instances, org structure, conference intake (consent, survey, badge reading, and self-enrollment from a booth QR code on the visitor’s own phone), billing, and the video-streaming control plane (edge-appliance + camera enrollment). Relays near-real-time session streaming to portals, and the synchronized start/stop control channel to edge agents, over Server-Sent Events — fanned out across pods via Postgres LISTEN/NOTIFY (S3 is the cross-pod chunk buffer).',
    libs: [
      { name: 'fastapi', what: 'HTTP framework (incl. StreamingResponse for the live-session SSE endpoint)' },
      { name: 'uvicorn[standard]', what: 'ASGI server' },
      { name: 'sqlalchemy ≥ 2.0', what: 'ORM + async sessions' },
      { name: 'asyncpg', what: 'Postgres driver — also LISTEN/NOTIFY for the live-session SSE relay' },
      { name: 'alembic', what: 'schema migrations (public + per-tenant)' },
      { name: 'pydantic v2 + pydantic-settings', what: 'request validation + env config' },
      { name: 'python-jose[cryptography]', what: 'JWT access + refresh tokens' },
      { name: 'bcrypt', what: 'password hashing' },
      { name: 'python-multipart', what: 'multipart/form-data parsing (file-upload endpoints: session attachments, device posts)' },
      { name: 'pyotp', what: 'TOTP 2FA (RFC 6238)' },
      { name: 'cryptography', what: 'Fernet symmetric encryption at rest (TOTP secrets + platform secrets — SMTP/LLM API keys)' },
      { name: 'aiosmtplib', what: 'SMTP mail transport — MailDev in local development; deployed environments send through the Mailgun HTTP API instead' },
      { name: 'email-validator', what: 'email syntax + domain validation (invitations, registration, profile)' },
      { name: 'boto3', what: 'S3 presigned PUT / GET URLs for capture bytes + session attachments + the live-stream chunk buffer' },
      { name: 'slowapi', what: 'rate limiting on sign-in, device enrollment and the public self-enrollment links' },
      { name: 'typer', what: 'operator CLI (app/cli.py — superadmin bootstrap, TOTP-key rotation)' },
      { name: 'stripe', what: 'billing surface (tenant subscriptions)' },
      { name: 'httpx', what: 'outbound HTTP — NPI lookup, internal probes, and the Mailgun HTTP-API mail provider' },
      { name: 'ffmpeg / ffprobe', what: 'MP4 metadata read for video-vs-EEG time-alignment on attachment confirm' },
    ],
  },
  {
    name: 'Insights worker',
    tag: 'Python · scientific stack',
    accent: 'secondary',
    icon: <MemoryIcon />,
    blurb:
      'Separate service, split into three independently-scaled pools by WORKER_JOB_CLASS. scoring runs cognitive (Welch-PSD focus / engagement / valence / workload) and motion (v2 drift-robust metrics — gyro angular-path, LDLJ smoothness, bidex_v2, tremor, ZUPT-bounded integration) scoring per phase, plus session_ready_email — the participant\u2019s \u201cyour data is ready\u201d email (or a booth visitor\u2019s one-time thank-you), sent seconds after scoring and retried until it goes out. cascade runs thumbnails, audio_sync (FFT cross-correlation of the capture-app anchor audio against uploaded video), compress, attachment_trim, retrace, the on-demand PDF reports (single-session + longitudinal, rendered HTML→PDF with WeasyPrint), and the zipped data exports (CSV/JSON). llm runs everything that waits on an outside service: marker_labels (the Whisper transcript of the anchor audio, sliced per focus marker), ai_coach (the AI-Coach narration by an OpenAI chat model, queued when scoring finishes) and coaching_transcript (full Whisper transcript of an attached coaching debrief, PHI-redacted; then, for opted-in tenants, the AI Coach debrief points — each quote must appear verbatim in the transcript or the point is dropped, and each is checked against the qualitative metrics fact sheet). Postgres-backed queue with LISTEN/NOTIFY for low-latency wake-up; the pool split keeps slow external AI calls from starving the scorecards.',
    libs: [
      { name: 'numpy', what: 'array primitives' },
      { name: 'scipy', what: 'signal processing — Butterworth bandpass + Welch PSD + IIR notch for the EEG pipeline; scipy.signal.correlate (FFT) for EEG↔video audio-sync alignment' },
      { name: 'ffmpeg / ffprobe', what: 'video frame seek, audio decode for EEG↔video sync, and per-marker anchor-audio clipping for the transcript labels — system binary, not a pip package' },
      { name: 'httpx', what: 'async HTTP client — OpenAI /audio/transcriptions (Whisper, always in English) and /chat/completions (AI coach, debrief points, badge reading)' },
      { name: 'sqlalchemy ≥ 2.0', what: 'async ORM for claiming insight_jobs rows + writing scores' },
      { name: 'asyncpg', what: 'Postgres driver — LISTEN/NOTIFY wake-up on insight_jobs_new' },
      { name: 'boto3', what: 'S3 — NDJSON downloads, thumbnail uploads, anchor-audio reads, PDF-report + data-export uploads, presigned-GET minting' },
      { name: 'weasyprint', what: 'HTML/CSS → PDF for the session + longitudinal reports (worker image only)' },
      { name: 'pydantic-settings', what: 'env config (DATABASE_URL, AWS_*, etc.) — shares the api config module' },
    ],
  },
  {
    name: 'Portal',
    tag: 'TypeScript · React 18 + MUI',
    accent: 'success',
    icon: <WebIcon />,
    blurb:
      'This SPA. Vite build, nginx-served in prod, axios against /api/v1. Role-gated client-side across superadmin, tenant_admin, coordinator and participant (the api enforces the real boundary), with MUI-tabbed admin consoles and camera / appliance management for the edge-video pipeline. Recharts for the Dashboard charts. Session replay — both post-session and near-real-time live (consumed over Server-Sent Events) — recomputes the capture-app DSP in the browser inside a Web Worker, including an interactive 3D hand-trajectory. The Analysis workspace overlays a whole team’s EEG focus, engagement, and hand-motion on one case against a shared wall-clock axis, plays each participant’s synced camera clip and 3D hand-path at any moment, and benchmarks a participant against reference cohorts.',
    libs: [
      { name: 'react + react-dom (18.3)', what: 'UI' },
      { name: '@mui/material + @mui/icons-material (v6)', what: 'design system' },
      { name: '@emotion/react + @emotion/styled', what: "MUI's CSS-in-JS styling engine" },
      { name: 'react-router-dom v7', what: 'client-side routing' },
      { name: 'axios', what: 'HTTP client (SSE live stream consumed via fetch + ReadableStream)' },
      { name: 'recharts (^3.8)', what: 'charts (Dashboard cognitive + motion, session-replay traces, the Analysis multi-participant focus/motion overlay)' },
      { name: 'three + @react-three/fiber + @react-three/drei', what: '3D hand-trajectory in the session-replay viewer + the Analysis moment inspector' },
      { name: '@tanstack/react-query', what: 'query caching where useful' },
      { name: 'qrcode.react', what: 'QR codes — TOTP enrollment, device enrollment, and the booth kiosk’s single-use self-enrollment code' },
      { name: 'libphonenumber-js', what: 'profile phone validation' },
      { name: 'vite (v8)', what: 'build tool + dev server — nginx serves the built bundle in prod' },
      { name: 'vitest (v4) + @testing-library/react', what: 'unit + component tests' },
    ],
  },
  {
    name: 'Capture-app',
    tag: 'TypeScript · Capacitor + React 19',
    accent: 'warning',
    icon: <TabletAndroidIcon />,
    blurb:
      'iOS / Android kiosk app that drives the Muse EEG headband and the two MetaMotion IMUs over BLE — packed-mode streaming for link stability — records NDJSON per session, and uploads to S3 gzipped (streamed while recording and compressed natively at upload, so long sessions never fill memory and a four-hour case leaves the phone as tens of megabytes rather than hundreds). Sensor capture runs in native code under a foreground service, so EEG and motion keep recording through a backgrounded, locked, or pocketed phone, and reconnect and resume after a mid-session sensor dropout. Madgwick sensor fusion (quaternion + linearAcceleration) now runs server-side by default; the app retains an in-TypeScript host-fusion mode for when on-device fusion is preferred.',
    libs: [
      { name: '@capacitor/core + ios + android (v7)', what: 'native runtime' },
      { name: '@capacitor-community/bluetooth-le', what: 'BLE GATT for Muse — patched with a native notification recorder so EEG keeps recording to disk while the app is backgrounded' },
      { name: 'metawear-capacitor', what: 'in-house wrapper around the MbientLab SDK' },
      { name: 'anchor-audio-capacitor', what: 'in-house plugin — native MediaRecorder for the silent EEG↔video audio-sync anchor, and the streaming file uploader that gzips a recording before PUTting it to S3. Bypasses the WebView audio stack (broken on OxygenOS) and the JS↔native binary bridge (corrupts Blob bodies).' },
      { name: '@capacitor-mlkit/barcode-scanning', what: 'device enrollment QR scan' },
      { name: '@capacitor/preferences', what: 'pinned device IDs + cached baselines' },
      { name: '@capacitor/filesystem', what: 'NDJSON writes' },
      { name: '@capacitor/device', what: 'platform metadata for crash reports' },
      { name: 'tweetnacl + tweetnacl-util', what: 'Curve25519 device keypair (enrollment)' },
      { name: 'fflate', what: 'gzips each live-stream chunk before it leaves the phone — the continuous half of the upload, sent while the operation is happening' },
      { name: 'react + react-dom (19)', what: 'UI' },
      { name: 'vite (v8)', what: 'builds the WebView bundle Capacitor ships inside the native app' },
    ],
  },
  {
    name: 'Edge appliance',
    tag: 'Go · ffmpeg · MediaMTX',
    accent: 'info',
    icon: <VideocamIcon />,
    blurb:
      'On-site box on the capture LAN that brings OR / lab camera video into the platform. A dependency-free Go control agent enrolls with the cloud and holds an outbound control stream (SSE) carrying the synchronized start / stop the cloud broadcasts as a capture session begins and ends, keeping camera recording aligned to the session for S3 upload. It records USB/UVC webcams, network RTSP cameras (e.g. the ORDRO EP8 head-cam it pulls directly), and RTMP-push cameras through a bundled ffmpeg — as crash-survivable fragmented MP4, with startup recovery of any clip a crash stranded. Live check (aim / frame a camera) is local-only by design: frames are served from the box’s own localhost and never relayed off-site, so a camera can’t be viewed from a remote computer. Ships as single cross-platform binaries (Windows / Linux / macOS), each bundling ffmpeg + MediaMTX.',
    libs: [
      { name: 'Go (standard library)', what: 'the control agent — enrollment, outbound control SSE, camera record/stop, and a localhost-only HTTP server for the on-box Live check; one dependency-free binary per OS' },
      { name: 'ffmpeg', what: 'bundled — records USB / RTSP / RTMP cameras to crash-survivable MP4, plus test snapshots and the local Live-check MJPEG burst' },
      { name: 'MediaMTX', what: 'adopted RTMP receiver / WebRTC for push cameras — a single external binary, not built in-house' },
      { name: 'RTMP / RTSP', what: 'camera video ingest — push (RTMP) + network-pull (RTSP)' },
    ],
  },
];

export function About() {
  const year = new Date().getFullYear();
  // Stack snapshot: tabbed instead of the original 4-card grid so a
  // long libs list doesn't push the team / copyright sections off
  // the fold. Tab index is local state — no need to deep-link, the
  // reader scans the cards sequentially when first arriving and
  // returns to "their" stack on follow-up visits.
  const [activeStackIdx, setActiveStackIdx] = useState(0);
  const activeStack = STACKS[activeStackIdx];
  // Live platform health. The endpoint is admin-scoped, so for non-admin
  // roles this simply stays null and the diagnostics panel doesn't render —
  // real status for those who can see it, nothing fabricated for those who
  // can't.
  const [status, setStatus] = useState<SystemStatusSnapshot | null>(null);
  useEffect(() => {
    let alive = true;
    getSystemStatus()
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, []);
  return (
    <Stack spacing={3}>
      <Typography variant="h4">About 10NEWTONS</Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body1">
          10NEWTONS is an enterprise platform for measuring, analyzing, and
          improving surgical and procedural performance. By combining
          wearable sensing, motion analysis, and AI-driven insights, it lets
          hospitals, researchers, and educators objectively evaluate
          procedural skill at scale.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Operators capture sessions on a mobile device with a Muse EEG
          headband and two MetaMotion IMUs, and in the operating room the
          on-site edge appliance records the cameras in step; the data flows here for
          participants, tenant admins, and 10NEWTONS staff to review trait
          scores, motion summaries, and per-procedure breakdowns, track
          progression over time, and download PDF reports or export the
          underlying data.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Build status, change-log, and the engineering README live in
          the source repository.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Have a bug, gap, or question? Reach the engineering team at{' '}
          <MuiLink href="mailto:help@10newtons.com">
            help@10newtons.com
          </MuiLink>
          .
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Meet the Team</Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />
        <Typography variant="body2">
          Meet the founders, clinical leadership, and engineering team behind
          10NEWTONS on{' '}
          <MuiLink
            href="https://10newtons.com/#team"
            target="_blank"
            rel="noopener noreferrer"
          >
            our company site
          </MuiLink>
          .
        </Typography>
      </Paper>

      {/* Live platform status — renders only when the (admin-scoped) health
          endpoint is reachable for this user. Real health, never faked. */}
      {status && status.services.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ flexWrap: 'wrap' }}
          >
            <Typography variant="h6">Platform status</Typography>
            <Typography variant="caption" color="text.secondary">
              Live · checked{' '}
              {formatTime(status.checked_at)}
            </Typography>
          </Stack>
          <Divider sx={{ mt: 1, mb: 2 }} />
          <Grid container spacing={1.5}>
            {status.services.map((s) => {
              const meta = STATUS_META[s.status];
              return (
                <Grid key={s.name} size={{ xs: 6, sm: 4, md: 3 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      height: '100%',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700 }}
                        noWrap
                      >
                        {s.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={meta.label}
                        color={meta.color}
                        sx={{ height: 20, fontWeight: 700 }}
                      />
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {[
                        s.version ? `v${s.version}` : null,
                        s.response_ms != null
                          ? `${Math.round(s.response_ms)} ms`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || s.detail}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      <Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="h6">Platform architecture</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          How data moves through the platform, and the major open-source
          libraries powering each component.
        </Typography>
        <ArchFlow />
        <Box sx={{ mt: 2 }}>
          <StackTabs
            stacks={STACKS}
            activeIdx={activeStackIdx}
            onChange={setActiveStackIdx}
          />
          <StackCard stack={activeStack} />
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">Copyright</Typography>
        <Divider sx={{ mb: 2, mt: 1 }} />
        <Typography variant="body2">
          © {year} 10 Newtons Inc. All rights reserved.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The 10NEWTONS platform incorporates open-source libraries
          listed above; each retains the copyright of its respective
          author and is used under its own license. License texts are
          shipped with each library distribution.
        </Typography>
      </Paper>
    </Stack>
  );
}

/**
 * Tab strip above the stack snapshot. Each tab carries the
 * stack's icon + name + monospace tag (e.g. "Python · FastAPI")
 * so the active stack's identity stays visible without the
 * card below needing its own header.
 *
 * Scrollable on narrow viewports so a fifth or sixth stack later
 * doesn't blow the layout up.
 */
function StackTabs({
  stacks,
  activeIdx,
  onChange,
}: {
  stacks: StackComponent[];
  activeIdx: number;
  onChange: (i: number) => void;
}) {
  const theme = useTheme();
  const activeAccent = theme.palette[stacks[activeIdx].accent];
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        // The accent of the active stack drives the indicator
        // colour so the tab strip + the card body read as one.
        '& .MuiTabs-indicator': {
          backgroundColor: activeAccent.main,
        },
      }}
    >
      <Tabs
        value={activeIdx}
        onChange={(_, v) => onChange(v as number)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {stacks.map((stack) => {
          const accent = theme.palette[stack.accent];
          return (
            <Tab
              key={stack.name}
              iconPosition="start"
              icon={
                <Avatar
                  sx={{
                    bgcolor: accent.main,
                    color: accent.contrastText,
                    width: 28,
                    height: 28,
                  }}
                >
                  {stack.icon}
                </Avatar>
              }
              label={
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2 }}
                  >
                    {stack.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {stack.tag}
                  </Typography>
                </Box>
              }
              sx={{
                textTransform: 'none',
                minHeight: 60,
                alignItems: 'center',
                gap: 1,
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
}

/**
 * The selected stack's body — blurb + libraries list. The header
 * (icon / name / tag) lives on the tab above, so this card
 * focuses on the content. Borders + the per-row lib name colour
 * still use the stack's accent for cohesion with the tab strip.
 */
function StackCard({ stack }: { stack: StackComponent }) {
  const theme = useTheme();
  const accent = theme.palette[stack.accent];
  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: alpha(accent.main, 0.25),
        borderTop: 'none',
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        p: 2,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {stack.blurb}
      </Typography>
      <Stack divider={<Divider flexItem />} spacing={0.75}>
        {stack.libs.map((lib) => (
          <Box
            key={lib.name}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 0, sm: 1 },
              alignItems: { sm: 'baseline' },
              py: 0.5,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                fontFamily: 'monospace',
                color: accent.main,
                flexShrink: 0,
                minWidth: { sm: 220 },
              }}
            >
              {lib.name}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              {lib.what}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
